import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getDomainById } from '../../data/domains';
import { Question, Standard, UserRole, Game } from '../../data/types';
import { OptionItem } from '../QuestionOptionInput';
import { AuthContext } from '../../App';
import { dataService } from '../../services/dataService';
import { RoleBasedComponent, RoleBasedButton } from '../common/RoleBasedComponent';
import { usePermissions } from '../../contexts/PermissionContext';

const StandardDetailScreen: React.FC = () => {
  const { standardId } = useParams<{ standardId: string }>();
  const navigate = useNavigate();
  const { activeRole } = useContext(AuthContext);
  const permissions = usePermissions();
  
  // Check permissions for standards
  const canEditStandards = permissions.canEdit('standards');
  const canViewStandards = permissions.canView('standards');

  const [standard, setStandard] = useState<Standard | null>(null);
  const [videoUrl, setVideoUrl] = useState('https://www.youtube.com/embed/dQw4w9WgXcQ'); // Placeholder video
  const [standardName, setStandardName] = useState('');
  const [standardDescription, setStandardDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState<'Easy' | 'Medium' | 'Hard'>('Medium');
  const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
  const [suggestedGames, setSuggestedGames] = useState<Game[]>([]);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});
  const [results, setResults] = useState<Record<string, boolean>>({});
  const [showResults, setShowResults] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [questionCount, setQuestionCount] = useState(5);
  const [availableQuestionCount, setAvailableQuestionCount] = useState<Record<string, number>>({
    Easy: 0,
    Medium: 0,
    Hard: 0
  });

  useEffect(() => {
    // Fetch data when component mounts or when standardId/difficulty changes
    const fetchData = async () => {
      try {
        // Fetch standards data
        const standards = await dataService.getStandards();
        const foundStandard = standards.find(s => s.id === standardId);
        
        if (foundStandard) {
          setStandard(foundStandard);
          setStandardName(foundStandard.code);
          setStandardDescription(foundStandard.description);
          
          // Fetch questions data
          const questions = await dataService.getQuestions();
          
          // Count questions available for each difficulty
          const easyQuestions = questions.filter(q => q.standardId === foundStandard.id && q.difficulty === 'Easy').length;
          const mediumQuestions = questions.filter(q => q.standardId === foundStandard.id && q.difficulty === 'Medium').length;
          const hardQuestions = questions.filter(q => q.standardId === foundStandard.id && q.difficulty === 'Hard').length;
          
          setAvailableQuestionCount({
            Easy: easyQuestions,
            Medium: mediumQuestions,
            Hard: hardQuestions
          });
          
          // Get initial questions for this standard
          const initialQuestions = questions
            .filter(q => q.standardId === foundStandard.id && q.difficulty === selectedDifficulty)
            .slice(0, questionCount);
          setGeneratedQuestions(initialQuestions);
          
          // Reset user answers and results when questions change
          setUserAnswers({});
          setResults({});
          setShowResults(false);
          setIsPreviewMode(false);
          
          // Fetch games data
          const games = await dataService.getGames();
          
          // Find suggested games based on standard ID and selected difficulty
          const relevantGames = games.filter(game =>
            game.supportedStandards.includes(foundStandard.id) &&
            game.difficulty === selectedDifficulty
          );
          setSuggestedGames(relevantGames);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    
    fetchData();
  }, [standardId, selectedDifficulty, questionCount]);

  const handleLoadQuestions = async () => {
    if (!standard) return;
    
    try {
      // Fetch questions data
      const questions = await dataService.getQuestions();
      
      // Filter questions by standard ID and difficulty
      const filteredQuestions = questions.filter(
        q => q.standardId === standard.id && q.difficulty === selectedDifficulty
      );
      
      // Limit to selected number of questions
      setGeneratedQuestions(filteredQuestions.slice(0, questionCount));
      
      // Reset user answers and results
      setUserAnswers({});
      setResults({});
      setShowResults(false);
      setIsPreviewMode(false);
    } catch (error) {
      console.error('Error loading questions:', error);
    }
  };

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handlePreview = () => {
    if (isPreviewMode) {
      // If already in preview mode, reset
      setIsPreviewMode(false);
      setUserAnswers({});
      setResults({});
      setShowResults(false);
    } else {
      // Enter preview mode
      setIsPreviewMode(true);
    }
  };

  const handleSubmitAnswers = () => {
    const newResults: Record<string, boolean> = {};

    generatedQuestions.forEach(question => {
      const userAnswer = userAnswers[question.id];
      if (userAnswer) {
        let correctAnswerStr = '';
        if (typeof question.correctAnswer === 'string') {
          correctAnswerStr = question.correctAnswer;
        } else if (Array.isArray(question.correctAnswer)) {
          correctAnswerStr = question.correctAnswer.join(',');
        }
        newResults[question.id] = userAnswer.toLowerCase() === correctAnswerStr.toLowerCase();
      }
    });

    setResults(newResults);
    setShowResults(true);
  };

  const handleSaveChanges = () => {
    if (!standard) return;

    setIsEditing(false);
    // In a real app, here you would make an API call to save the changes
    console.log('Saving changes:', {
      standardId: standard.id,
      code: standardName,
      description: standardDescription,
      videoUrl,
      questions: generatedQuestions
    });
  };

  const handleGoBack = () => {
    navigate('/standards');
  };

  if (!standard) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-700">Loading standard details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <button
            onClick={handleGoBack}
            className="mr-4 text-gray-600 hover:text-gray-900"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Standard Details</h1>
        </div>
        <div>
          {isEditing ? (
            <div className="flex space-x-2">
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-700"
              >
                Save Changes
              </button>
            </div>
          ) : (
            <RoleBasedComponent
              resource="standards"
              requiredPermission="edit"
              tooltip="You don't have permission to edit standards"
            >
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-700"
              >
                Edit Standard
              </button>
            </RoleBasedComponent>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
        {/* Standard Info Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-5">
            <div className="inline-block bg-primary-100 text-primary px-3 py-1 rounded-full text-sm font-medium mb-2">
              {standard.grade === 'K' ? 'Kindergarten' : `Grade ${standard.grade}`}
            </div>

            {isEditing ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="standard-code" className="block text-sm font-medium text-gray-700 mb-1">
                    Standard Code
                  </label>
                  <input
                    id="standard-code"
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    value={standardName}
                    onChange={(e) => setStandardName(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="standard-description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    id="standard-description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    rows={4}
                    value={standardDescription}
                    onChange={(e) => setStandardDescription(e.target.value)}
                  />
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-xl font-bold text-primary mb-1">{standardName}</h2>
                <p className="text-gray-800 mb-2">{standardDescription}</p>
              </>
            )}

            <div className="flex flex-wrap gap-2 mt-3">
              <div className="bg-gray-100 px-3 py-1 rounded-lg text-sm text-gray-700">
                Domain: {getDomainById(standard.domainId)?.name} {getDomainById(standard.domainId) && `(${getDomainById(standard.domainId)?.code})`}
              </div>
              <div className="bg-gray-100 px-3 py-1 rounded-lg text-sm text-gray-700">
                Cluster: {standard.cluster}
              </div>
            </div>
          </div>
        </div>

        {/* Video Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-3">Instructional Video</h3>
            <div className="flex justify-center mb-3">
              <div className="w-full max-w-md">
                <div className="relative" style={{ paddingTop: '75%' }}>
                  <iframe
                    className="absolute inset-0 w-full h-full rounded-lg border border-gray-200"
                    src={videoUrl}
                    title="Standard Instructional Video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>

            {isEditing && (
              <div>
                <label htmlFor="video-url" className="block text-sm font-medium text-gray-700 mb-1">
                  Video URL
                </label>
                <input
                  id="video-url"
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="Enter YouTube embed URL"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Question Preview Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-5">
          <div className="flex flex-wrap justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Standard Questions</h3>
              <p className="text-sm text-gray-500">
                Available questions: {availableQuestionCount[selectedDifficulty]} {selectedDifficulty} questions
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-700 mr-2">
                  Difficulty:
                </label>
                <div className="flex">
                  {(['Easy', 'Medium', 'Hard'] as const).map((difficulty) => (
                    <button
                      key={difficulty}
                      className={`px-3 py-1 text-sm font-medium rounded-lg ${
                        selectedDifficulty === difficulty
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      onClick={() => setSelectedDifficulty(difficulty)}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center">
                <label className="text-sm font-medium text-gray-700 mr-2">
                  Questions:
                </label>
                <select
                  className="bg-white border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-primary focus:border-primary text-sm"
                  value={questionCount}
                  onChange={(e) => setQuestionCount(Number(e.target.value))}
                >
                  {[3, 5, 10, 15, 20].map(count => (
                    <option key={count} value={count}>{count}</option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleLoadQuestions}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition"
              >
                Load Questions
              </button>
            </div>
          </div>

          {generatedQuestions.length > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                {generatedQuestions.map((question, index) => (
                  <div
                    key={question.id}
                    className={`bg-gray-50 p-4 rounded-lg ${
                      showResults
                        ? results[question.id]
                          ? 'border-2 border-green-400'
                          : userAnswers[question.id]
                            ? 'border-2 border-red-400'
                            : ''
                        : ''
                    }`}
                  >
                    <p className="font-medium">Question {index + 1}: {question.text}</p>
                    {question.type === 'MultipleChoice' && question.options && (
                      <div className="mt-2 ml-4 space-y-1">
                        {question.options.map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center">
                            <input
                              type="radio"
                              id={`q${index}-opt${optIndex}`}
                              name={`question-${index}`}
                              className="h-4 w-4 text-primary focus:ring-primary"
                              onChange={() => handleAnswerChange(question.id, typeof option === 'object' && 'text' in option ? (option as OptionItem).text : option as string)}
                              checked={userAnswers[question.id] === (typeof option === 'object' && 'text' in option ? (option as OptionItem).text : option as string)}
                            />
                            <label
                              htmlFor={`q${index}-opt${optIndex}`}
                              className="ml-2 text-gray-700"
                            >
                              {typeof option === 'object' && 'text' in option ? (option as OptionItem).text : option as string}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                    {question.type === 'TypeIn' && (
                      <div className="mt-2 ml-4">
                        <input
                          type="text"
                          className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                          placeholder="Type your answer here"
                          onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                          value={userAnswers[question.id] || ''}
                        />
                      </div>
                    )}
                    {question.type === 'DragAndDrop' && question.options && (
                      <div className="mt-2 ml-4">
                        <p className="text-sm text-gray-600 mb-2">Drag items to match them correctly</p>
                        <div className="space-y-2">
                          {question.options.map((optionPair, optIndex) => {
                            // Handle both string and OptionItem types
                            const pairText = typeof optionPair === 'object' && 'text' in optionPair ? 
                              (optionPair as OptionItem).text : optionPair as string;
                            const [term, definition] = pairText.split(':');
                            return (
                              <div key={optIndex} className="flex flex-col sm:flex-row sm:items-center gap-2">
                                <div className="bg-blue-100 p-2 rounded border border-blue-300 cursor-move flex-1">
                                  {term}
                                </div>
                                <div className="hidden sm:block text-gray-400">→</div>
                                <div className="bg-gray-100 p-2 rounded border border-gray-300 flex-1">
                                  {definition}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {showResults && results[question.id] !== undefined && (
                      <div className={`mt-2 text-sm font-semibold ${results[question.id] ? 'text-green-600' : 'text-red-600'}`}>
                        {results[question.id] ? 'Correct!' : `Incorrect. The correct answer is: ${question.correctAnswer}`}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end space-x-2">
                <RoleBasedComponent
                  resource="standards"
                  requiredPermission="view"
                  tooltip="You don't have permission to preview practice mode"
                >
                  <button
                    onClick={handlePreview}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-700 transition"
                  >
                    {isPreviewMode ? 'Exit Preview' : 'Preview Practice Mode'}
                  </button>
                </RoleBasedComponent>
                
                {isPreviewMode && (
                  <RoleBasedComponent
                    resource="standards"
                    requiredPermission="view"
                  >
                    <button
                      onClick={handleSubmitAnswers}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                    >
                      {showResults ? 'Try Again' : 'Submit Answers'}
                    </button>
                  </RoleBasedComponent>
                )}
              </div>
            </>
          ) : (
            <p className="text-gray-500">
              No questions available for this standard and difficulty level.
            </p>
          )}
        </div>
      </div>

      {/* Suggested Games Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-5">
          <h3 className="text-lg font-semibold mb-4">Suggested Games</h3>
          
          {suggestedGames.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {suggestedGames.map(game => (
                <div key={game.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition">
                  <img
                    src={game.thumbnail}
                    alt={game.title}
                    className="w-full h-32 object-cover"
                  />
                  <div className="p-4">
                    <h4 className="font-medium text-gray-900">{game.title}</h4>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="px-2 py-1 bg-gray-100 text-xs rounded-full text-gray-600">{game.type}</span>
                      <span className="px-2 py-1 bg-gray-100 text-xs rounded-full text-gray-600">{game.difficulty}</span>
                    </div>
                    <RoleBasedComponent
                      resource="games"
                      requiredPermission="view"
                      tooltip="You don't have permission to play games"
                    >
                      <button className="mt-3 text-sm text-primary font-medium hover:text-primary-700">
                        Play Game
                      </button>
                    </RoleBasedComponent>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">
              No games available for the selected difficulty level. Try selecting a different difficulty.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandardDetailScreen; 